<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb />
      </div>
    </div>
    <b-card>
      <h4 class="mb-2 mb-xl-0 mr-2 font-weight-bold">會員等級條件</h4>
      <div class="row my-3">
        <div class="col-12 col-xl-4"></div>
        <div
          class="
            col-12 col-xl-8
            d-flex flex-xl-row flex-column
            align-items-start align-items-xl-center
            justify-content-end
          "
        >
          <b-button
            v-if="checkPermission([consts.CUSTOMER_LEVEL_RULE_CREATE])"
            class="flex-shrink-0 mb-2 mb-xl-0 mr-2"
            variant="primary"
            @click="goToCreatePage"
          ><i class="fa fa-plus"></i>新增會員等級條件</b-button>
          <b-form-select
            v-model="query.is_enabled"
            :options="enableStatusOptions"
            class="mb-xl-0 w-50"
            placeholder="請選擇啟用狀態"
            @change="handlePageReset"
          ></b-form-select>
          <b-form-select
            v-model="query.level_id"
            :options="customerLevelOptions"
            class="mb-xl-0 w-50"
            placeholder="請選擇會員等級"
            @change="handlePageReset"
          ></b-form-select>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <b-table
            striped
            hover
            responsive
            :items="rules"
            :fields="fields"
            :busy="isLoading"
          >
            <template #table-busy>
              <div class="text-center my-2">
                <b-spinner class="align-middle"></b-spinner>
              </div>
            </template>

            <template #cell(from_level)="data">
              <span v-if="data.item.from_level">
                {{ data.item.from_level.name }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>

            <template #cell(to_level)="data">
              <span v-if="data.item.level">
                {{ data.item.level.name }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>

            <template #cell(time_range)="data">
              <span v-html="formatTimeRange(data.item.rule)"></span>
            </template>

            <template #cell(calculations)="data">
              <span v-html="formatCalculations(data.item.rule)"></span>
            </template>

            <template #cell(is_enabled)="data">
              <b-badge
                :variant="
                  !!data.item.is_enabled === true ? 'success' : 'danger'
                "
                class="mr-1"
              >{{
                  !!data.item.is_enabled === true ? "啟用" : "停用"
                }}</b-badge
              >
            </template>

            <template #cell(actions)="data">
              <b-button
                v-if="checkPermission([consts.CUSTOMER_LEVEL_RULE_LIST])"
                class="ml-2"
                variant="inverse-primary"
                :to="{
                  name: 'CustomerLevelRuleView',
                  params: { id: data.item.id },
                }"
                ><span class="mdi mdi-eye"></span> 查看</b-button
              >

              <b-button
                v-if="checkPermission([consts.CUSTOMER_LEVEL_RULE_EDIT])"
                class="ml-2"
                variant="inverse-warning"
                :to="{
                  name: 'CustomerLevelRuleEdit',
                  params: { id: data.item.id },
                }"
                ><span class="mdi mdi-lead-pencil"></span> 編輯</b-button
              >

              <b-button
                v-if="checkPermission([consts.CUSTOMER_LEVEL_RULE_DELETE])"
                class="ml-2"
                variant="inverse-danger"
                @click="handleDelete(data.item)"
                ><span class="mdi mdi-delete"></span> 刪除</b-button
              >
            </template>
          </b-table>
        </div>
      </div>

      <CustomPagination
        :currentPage="query.page"
        :totalRows="totalRows"
        :perPage="query.per_page"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
      />
    </b-card>
  </div>
</template>

<script>
import { mapState } from "vuex";
import customerLevelRuleApi from "@/apis/customer-level-rules";
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import "bootstrap-vue/dist/bootstrap-vue.css";
import { paginationMixin } from "@/mixins/pagination";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import { updateQueryFromRoute, updateUrl } from "@/utils/updateUrl";
import { pickBy } from "lodash";
import customerLevelApi from "@/apis/customer-levels";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: { CustomPagination, BreadCrumb },
  mixins: [paginationMixin],
  data() {
    return {
      consts,
      isLoading: false,
      totalRows: 0,
      enableStatusOptions: [
        { value: 1, text: "已啟用" },
        { value: 0, text: "已停用" },
      ],
      customerLevelOptions: [{ value: null, text: "全部會員等級" }],
      rules: [],
      fields: [
        { key: "order", label: "順序", sortable: true },
        {
          key: "type",
          label: "類型",
          formatter: (value) => {
            const typeMap = {
              direct: "直升",
              upgrade: "升等",
              keep: "續等",
            };
            return typeMap[value] || value;
          },
        },
        { key: "from_level", label: "原始等級" },
        { key: "to_level", label: "目標等級" },
        { key: "time_range", label: "時間範圍" },
        { key: "calculations", label: "計算條件" },
        { key: "is_enabled", label: "狀態" },
        { key: "actions", label: "管理" },
      ],
      initialized: false,
      query: {
        page: 1,
        per_page: 10,
        level_id: null,
        is_enabled: 1
      },
    };
  },

  computed: {
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
  },

  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrl(this.query, this.$store, this.$router);
      },
    },
    $route(to) {
      if (!this.initialized) return;
      Object.keys(to.query).forEach((key) => {
        this.query[key] = to.query[key] || null;
      });
      this.getRuleList();
    },
  },

  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
    this.init();
  },

  methods: {
    async init() {
      await this.getCustomerLevelsList();
      await this.getRuleList();
      this.initialized = true;
    },

    /**
     * 格式化數字（加上千分位）
     */
    formatNumber(value) {
      if (value === null || value === undefined) return "0";
      return Number(value).toLocaleString();
    },

    /**
     * 將時間範圍轉換為人類可讀文字
     */
    formatTimeRange(rule) {
      if (!rule || !rule.time_range) {
        return '<span class="text-muted">-</span>';
      }

      const timeRange = rule.time_range;
      const type = timeRange.type;

      let text = "";

      switch (type) {
        case "forever":
          text = "無限期";
          break;

        case "annual_period":
          text = "當年度<br><small class='text-muted'>(1/1 ~ 12/31)</small>";
          break;

        case "monthly_period":
          text = "當月";
          break;

        case "rolling_days":
          text = `過去 ${timeRange.days || 365} 天`;
          break;

        case "custom_range":
          if (timeRange.start && timeRange.end) {
            text = `${timeRange.start}<br>~<br>${timeRange.end}`;
          } else {
            text = "自訂區間";
          }
          break;

        default:
          text = '<span class="text-muted">-</span>';
      }

      // 加上會籍起始日標記
      if (rule.use_membership_start_date) {
        text += '<br><small class="text-info">(從會籍開始)</small>';
      }

      return text;
    },

    /**
     * 將計算條件轉換為人類可讀文字
     */
    formatCalculations(rule) {
      if (!rule || !rule.calculations || rule.calculations.length === 0) {
        return '<span class="text-muted">-</span>';
      }

      const calculations = rule.calculations;
      const logic = rule.calculation_logic || "AND";

      const descriptions = calculations.map((calc) =>
        this.formatSingleCalculation(calc)
      );

      if (descriptions.length === 1) {
        return descriptions[0];
      }

      // 多條件時顯示邏輯關係
      const logicText = logic === "OR" ? "或" : "且";
      const logicBadge =
        logic === "OR"
          ? '<span class="badge badge-warning">OR</span>'
          : '<span class="badge badge-info">AND</span>';

      return `${logicBadge}<br>${descriptions.join(`<br><small class="text-muted">${logicText}</small><br>`)}`;
    },

    /**
     * 格式化單一計算條件
     */
    formatSingleCalculation(calc) {
      if (!calc || !calc.strategy) {
        return '<span class="text-muted">-</span>';
      }

      const strategy = calc.strategy;
      let text = "";

      switch (strategy) {
        case "order_sum":
          text = `累積消費 NT$ ${this.formatNumber(calc.amount)}`;
          break;

        case "order_single_max":
          text = `單筆消費 NT$ ${this.formatNumber(calc.amount)}`;
          break;

        case "order_count":
          text = `消費 ${this.formatNumber(calc.count)} 次`;
          break;

        case "order_count_min_amount":
          text = `消費 ${this.formatNumber(calc.count)} 次<br><small class="text-muted">(每次 ≥ NT$ ${this.formatNumber(calc.min_amount)})</small>`;
          break;

        case "point_balance":
          text = `點數餘額 ≥ ${this.formatNumber(calc.amount)} 點`;
          break;

        case "form_result_count":
          text = `表單填寫 ${this.formatNumber(calc.count)} 次`;
          break;

        case "order_item_count":
          text = `購買商品 ${this.formatNumber(calc.count)} 項`;
          break;

        default:
          text = strategy;
      }

      // 顯示訂單狀態過濾
      if (calc.filters && calc.filters.status && calc.filters.status.length > 0) {
        const statusMap = {
          completed: "已完成",
          shipped: "已出貨",
          paid: "已付款",
        };
        const statusTexts = calc.filters.status.map(
          (s) => statusMap[s] || s
        );
        text += `<br><small class="text-muted">(${statusTexts.join("/")})</small>`;
      }

      return text;
    },

    async getRuleList() {
      try {
        this.isLoading = true;

        const params = pickBy(this.query, (value) => value != null);
        const { data } = await customerLevelRuleApi.getRules(
          this.organization,
          params
        );

        this.rules = data.data;
        this.totalRows = data.meta.total;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    async getCustomerLevelsList() {
      try {
        this.isLoading = true;
        const { data } = await customerLevelApi.list({ all: true });

        this.customerLevelOptions = this.customerLevelOptions.concat(
          data.data.map((level) => ({
            value: level.id,
            text: level.name,
          }))
        );
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    async handleDelete(level) {
      const levelName = level.level ? level.level.name : "此";
      let confirm = await this.$swal.fire({
        type: "warning",
        title: "刪除會員等級規則",
        text: `您確認要刪除 ${levelName} 這個會員等級規則嗎？`,
        confirmButtonText: "確認",
        cancelButtonText: "取消",
        showCancelButton: true,
      });

      if (!confirm.value) return;

      try {
        await customerLevelRuleApi.removeRule(this.organization, level.id);
        this.$swal("刪除成功", "", "success");
        await this.getRuleList();
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "", "error");
      }
    },

    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },

    goToCreatePage() {
      this.$router.push({
        name: "CustomerLevelRuleCreate",
        query: { 
          level_id: this.query.level_id,
          is_enabled: this.query.is_enabled
        },
      });
    },
  },
};
</script>

<style scoped>
.badge {
  font-size: 0.7rem;
}
</style>
